// "Out of sync / Render & Reload", extracted from the Load Balancer command bar.
//
// Enabling a worker or nudging its weight dirties the nginx config, and both of
// those now happen on the Workers page — so the control that pushes the change
// live has to be reachable from there too, not only from the page the pool used
// to live on. Owns its own drawer state so a caller only has to render it.

import { useQuery } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import { cn } from "@veil/ui/lib/utils";
import PlayCircle from "lucide-react/dist/esm/icons/play-circle";
import { useState } from "react";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";
import { DirtyBadge } from "@/pages/cluster/dirty-badge";
import { RenderDrawer } from "@/pages/cluster/render-drawer";
import type { ClusterResponse, ClusterStats } from "@/pages/cluster/types";

const CLUSTER_REFETCH_MS = 30_000;

/** Shared query, so the Workers page and ClusterShell cost one request. */
export function useClusterState() {
	return useQuery({
		queryKey: ["cluster"],
		queryFn: () => unwrap(api.api.cluster.get()) as Promise<ClusterResponse>,
		refetchInterval: CLUSTER_REFETCH_MS,
	});
}

export function RenderStatusBar({
	stats,
	clusterName,
	className,
}: {
	stats: ClusterStats | undefined;
	clusterName: string;
	className?: string;
}) {
	const { user } = useAuth();
	const canEdit = hasRole(user, "operator");
	const canApply = hasRole(user, "admin");
	const [renderOpen, setRenderOpen] = useState(false);

	const dirty = stats?.dirty === true;
	const renderTitle = canApply
		? dirty
			? "Validate, swap, and reload openresty"
			: "Open preview drawer"
		: canEdit
			? "Admin role required to apply"
			: "Operator role required";

	return (
		<div className={cn("flex items-center gap-4", className)}>
			<div className="flex min-w-[12rem] flex-col justify-center text-xs leading-tight text-muted-foreground">
				<span
					className={cn(
						"font-mono text-[0.68rem] uppercase tracking-[0.16em]",
						dirty
							? "text-amber-700 dark:text-amber-400"
							: "text-emerald-700 dark:text-emerald-400",
					)}
				>
					{dirty ? "Out of sync" : "Synced"}
				</span>
				<span className="truncate text-xs text-foreground/80">
					<DirtyBadge stats={stats} />
				</span>
			</div>

			<Button
				size="lg"
				variant={dirty ? "default" : "outline"}
				onClick={() => setRenderOpen(true)}
				disabled={!canApply}
				title={renderTitle}
				className={cn(
					dirty &&
						canApply &&
						"bg-amber-600 text-white shadow-sm hover:bg-amber-600/90 dark:bg-amber-500 dark:text-amber-50 dark:hover:bg-amber-500/90",
				)}
			>
				<PlayCircle data-icon="inline-start" />
				{canApply ? "Render & Reload" : "Render (admin only)"}
			</Button>

			<RenderDrawer
				open={renderOpen}
				onOpenChange={setRenderOpen}
				stats={stats}
				canApply={canApply}
				canEdit={canEdit}
				clusterName={clusterName}
			/>
		</div>
	);
}
